/**
 * PieCharts_V2 - Consumption and Cost Pie Charts for V2 Data
 *
 * Displays two pie charts:
 * 1. Total Consumption by Device (kWh)
 * 2. Total Cost by Device (cents)
 *
 * Works with V2 API data structure (metadata.thermostat_details and metadata.actor_details)
 */

import React, { useState, useEffect, useMemo } from "react";
import { PieChart, Pie, Cell, Legend, Tooltip, ResponsiveContainer } from "recharts";

const COLORS = [
  "#8884d8", "#82ca9d", "#ffc658", "#ff8042",
  "#a4de6c", "#d0ed57", "#8dd1e1", "#83a6ed",
  "#ffbb28", "#ff7300", "#00c49f", "#ff9999"
];

const PieCharts_V2 = ({ data, metadata }) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Extract thermostat and actor details from metadata
  const thermostatDetails = metadata?.thermostat_details || {};
  const actorDetails = metadata?.actor_details || {};

  // Combine thermostatDetails and actorDetails into a single set of unique devices
  const allDevices = useMemo(() => {
    return [
      ...Object.keys(thermostatDetails),
      ...Object.keys(actorDetails)
    ].filter((key, index, self) => !key.includes("#config") && self.indexOf(key) === index);
  }, [thermostatDetails, actorDetails]);

  // Create a fixed color mapping for each device
  const colorMapping = useMemo(() => {
    return allDevices.reduce((acc, device, index) => {
      acc[device] = COLORS[index % COLORS.length];
      return acc;
    }, {});
  }, [allDevices]);

  // Generate pie chart data while ensuring consistent colors
  const generatePieData = (source, valueKey) =>
    Object.keys(source || {})
      .filter(key => !key.includes("#config"))
      .map(key => ({
        name: source[key]?.name || "Unknown",
        value: source[key]?.[valueKey] || 0,
        color: colorMapping[key]
      }));

  // Combine and sort the data by consumption (biggest first)
  const consumptionData = useMemo(() => {
    return [
      ...generatePieData(thermostatDetails, "total_consumption"),
      ...generatePieData(actorDetails, "total_consumption")
    ]
      .filter(item => item.value > 0) // Only include items with consumption
      .sort((a, b) => b.value - a.value);
  }, [thermostatDetails, actorDetails, colorMapping]);

  // Sort cost data in the same order as consumption data
  const costData = useMemo(() => {
    return [
      ...generatePieData(thermostatDetails, "total_cost"),
      ...generatePieData(actorDetails, "total_cost")
    ]
      .filter(item => item.value > 0) // Only include items with cost
      .sort((a, b) => {
        const aIndex = consumptionData.findIndex(item => item.name === a.name);
        const bIndex = consumptionData.findIndex(item => item.name === b.name);
        return aIndex - bIndex;
      });
  }, [thermostatDetails, actorDetails, consumptionData, colorMapping]);

  // Custom legend formatter
  const renderLegend = (value, entry) => `${entry.payload.name}: ${entry.payload.value.toFixed(2)}`;

  // Responsive sizing
  const dynamicChartWidth = isMobile ? 400 : 600;
  const dynamicChartHeight = isMobile ? 500 : 450;
  const dynamicOuterRadius = isMobile ? 100 : 140;

  const dynamicLegendLayout = isMobile ? "horizontal" : "vertical";
  const dynamicAlign = isMobile ? "center" : "right";
  const dynamicVerticalAlign = isMobile ? "bottom" : "middle";

  const dynamicLegendWrapperStyle = {
    marginTop: 10,
    whiteSpace: "normal",
    maxWidth: "100%",
  };

  // Check if we have data to display
  if (Object.keys(thermostatDetails).length === 0 && Object.keys(actorDetails).length === 0) {
    return <div>No data available for pie charts.</div>;
  }

  if (consumptionData.length === 0 || costData.length === 0) {
    return <div>No valid data available to render pie charts.</div>;
  }

  return (
    <div className="pie-chart-container">
      <div className="pie-chart">
        <h4>Total Consumption by Device (kWh)</h4>
        <ResponsiveContainer width="100%" height={dynamicChartHeight}>
          <PieChart width={dynamicChartWidth} height={dynamicChartHeight}>
            <Pie
              data={consumptionData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={dynamicOuterRadius}
            >
              {consumptionData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Legend
              layout={dynamicLegendLayout}
              verticalAlign={dynamicVerticalAlign}
              align={dynamicAlign}
              wrapperStyle={dynamicLegendWrapperStyle}
              formatter={renderLegend}
            />
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      <div className="pie-chart">
        <h4>Total Cost by Device (cents)</h4>
        <ResponsiveContainer width="100%" height={dynamicChartHeight}>
          <PieChart width={dynamicChartWidth} height={dynamicChartHeight}>
            <Pie
              data={costData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={dynamicOuterRadius}
            >
              {costData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Legend
              layout={dynamicLegendLayout}
              verticalAlign={dynamicVerticalAlign}
              align={dynamicAlign}
              wrapperStyle={dynamicLegendWrapperStyle}
              formatter={renderLegend}
            />
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PieCharts_V2;
